import ToastAlert from '@/Components/Notifications/ToastAlert.vue'
import { router } from '@inertiajs/vue3'
import { markRaw } from 'vue'
import { toast } from 'vue-sonner'

export const TOAST_DURATION = 5000
export const TOAST_DURATION_WITH_ACTION = 8000

/** How often a toast that is still on screen was raised again, by toast id. */
const repeats = new Map()

const toastId = (type, title, description) =>
  `${type}:${title}:${description ?? ''}`

/**
 * An action button: a callback, a link (bell notifications) or a backend route the toast calls
 * (bannerNotification's `action`).
 *
 * @param {{label: string, onClick?: Function, href?: string, target?: string, url?: string, method?: string, data?: object}|null} action
 */
const toSonnerAction = (action) => {
  if (!action?.label) {
    return undefined
  }

  return {
    label: action.label,
    onClick: () => {
      if (typeof action.onClick === 'function') {
        action.onClick()
        return
      }
      if (action.href) {
        window.open(action.href, action.target ?? '_blank', 'noopener')
        return
      }
      if (action.url) {
        router.visit(action.url, {
          method: action.method ?? 'post',
          data: action.data ?? {},
          preserveScroll: true,
        })
      }
    },
  }
}

/**
 * Shows a toast in the design-system look (AppToaster, bottom centre).
 *
 * One sentence that says what happened; a headline only when the backend sends one that differs.
 * Success and info leave after 5 s (8 s with an action), errors stay until closed. The same
 * message raised again while it is on screen counts up instead of stacking.
 *
 * @param {{type?: 'success'|'info'|'error', message?: string|null, headline?: string|null, action?: object|null}} notification
 * @returns {string|null} the toast id, or null when there was nothing to show
 */
export const notify = ({
  type = 'success',
  message = null,
  headline = null,
  action = null,
}) => {
  const kind = ['success', 'info', 'error'].includes(type) ? type : 'success'
  const hasHeadline = Boolean(headline) && headline !== message
  const title = hasHeadline ? headline : message
  const description = hasHeadline ? message : null

  if (!title) {
    return null
  }

  const id = toastId(kind, title, description)
  const isOnScreen = toast.getToasts().some((item) => item.id === id)
  const count = isOnScreen ? (repeats.get(id) ?? 1) + 1 : 1
  repeats.set(id, count)

  const text = count > 1 ? `${title} (${count})` : title
  const sonnerAction = toSonnerAction(action)

  toast[kind](kind === 'error' ? markRaw(ToastAlert) : text, {
    id,
    description: description ?? undefined,
    componentProps: kind === 'error' ? { text } : undefined,
    action: sonnerAction,
    duration:
      kind === 'error'
        ? Infinity
        : sonnerAction
          ? TOAST_DURATION_WITH_ACTION
          : TOAST_DURATION,
    onDismiss: () => repeats.delete(id),
    onAutoClose: () => repeats.delete(id),
  })

  return id
}
